import { useState, useContext, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { HeadingText, UBButton } from "../../../../Libraries";
import {
  NumKeypad,
  TwoColumnWrapperHeader,
  twoColumnWrapperHeaderStyles,
} from "./../../../common";
import {
  ObserveSaleOrderLineBySaleOrder,
  deleteAllSaleOrderLineById,
} from "../../../../database/helpers/sale_order_line";
import { bulkCreateOrdersV3 } from "../../../../database/helpers/orders";
import { bulkUpdateStockV2 } from "./../../../../database/helpers/stock";
import withObservables from "@nozbe/with-observables";
import { UdhaarCheckout, OnlinePaymentCheckOut } from "..";
import { uuid } from "../../../../oscar-pos-core/constants";
import { ReactComponent as SuccessPaymentIcon } from "./../../../../assets/icons/success-payment.svg";
import styles from "./styles.module.css";
import { Sync } from "../../../../SyncService";
import { CardNewIcon, CashNewIcon, UdhaarNewIcon } from "../../../../assets/icons/icon";
import { deleteSaleOrderById } from "../../../../database/helpers/sale_order";
import { useNavigate } from "react-router-dom";
import { analytics, triggerLogEvent } from "../../../../config/firebase";

import { getAllTransactionCount } from "../../../../database/helpers/pos_txns";
import { useDemoModeNavigation } from "../../../../hooks/useDemoNavigation";
import { formattedCount } from "../../../../utils";
import { bulkCreateInventoryV3 } from "../../../../database/helpers/inventory";

const paymentOption = {
  cash: "udhaar",
  udhaar: "online",
  online: "cash",
};
const CheckoutOrder = ({
  activeSaleOrder,
  totalAmount,
  business,
  user,
  sale_order_line,
  customer,
}) => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [activePaymentMethod, setActivePaymentMethod] = useState("cash");
  const [isLoading, setIsLoading] = useState(false);
  const { handleDemoNavigation } = useDemoModeNavigation();
  const isFocussedRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.shiftKey && event.key === "Enter") {
        event.preventDefault();
        if (
          (activePaymentMethod === "udhaar" && customer) ||
          activePaymentMethod == "online"
        )
          confirmSale();
      }
      if (event.code === "Space" && !isFocussedRef.current) {
        event.preventDefault();
        if (paymentOption[activePaymentMethod] === "udhaar")
          triggerLogEvent(analytics, "pos_payment_udhaar");
        if (paymentOption[activePaymentMethod] === "online")
          triggerLogEvent(analytics, "pos_payment_online");
        if (paymentOption[activePaymentMethod] === "cash")
          triggerLogEvent(analytics, "pos_payment_cash");

        setActivePaymentMethod(paymentOption[activePaymentMethod]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activePaymentMethod, customer]);

  const confirmSale = async (changeAmount, newAddCustomer) => {
    triggerLogEvent(analytics, "pos_payment_charge");
    setIsLoading(true);
    const nextInvoiceCount = Number(business?.invoice_number_count || 0) + 1;
    try {
      if (activePaymentMethod == "cash")
        triggerLogEvent(analytics, "pos_payment_cash_amount");

      let params = {
        status:
          activePaymentMethod == "cash" || activePaymentMethod == "online"
            ? "paid"
            : "unpaid",
        discount_unit: activeSaleOrder?.discount_unit || "rs",
        created_at: new Date(),
        date: new Date(),
        amount: Number(totalAmount || 0),
        type: "in",
        customer: newAddCustomer || customer,
        discount: Number(activeSaleOrder?.discount || 0),
        note: activeSaleOrder?.note ?? "",
        change_amount: Number(changeAmount || 0),
        tax_rate: Number(activeSaleOrder?.tax || 0),
        tax_title: activeSaleOrder?.tax_type,
        id: uuid(),
        invoice_number: `${formattedCount(nextInvoiceCount)}`,
        invoice_prefix: "POS#",
        tranType: "pos",
        method: activePaymentMethod,
      };

      let invoice = await business.createInvoice(params);
      await business.updateBusinessInvoiceNo(nextInvoiceCount);

      let items = sale_order_line.map((item) => {
        return { ...item._raw, id: item._raw.stock_id || item._raw.id };
      });
      let stockItems = items.filter((x) => !x.is_open_item);
      if (items.length) {
        const ordersByItemId = await bulkCreateOrdersV3(
          business,
          invoice,
          items,
          "in",
          "pos");
        await deleteSaleOrderById(activeSaleOrder?.id);

        if (stockItems.length) {
          const inv = await bulkCreateInventoryV3(business, invoice, stockItems, 'out', 'pos', ordersByItemId);
          await bulkUpdateStockV2(stockItems, "in");
        }


        await deleteAllSaleOrderLineById(activeSaleOrder?.id);
      }

      if (customer && activePaymentMethod == "udhaar") {
        await addCustomerToTrns(invoice, newAddCustomer || customer);
        showSnackbar({
          message: I18n.udhaar_payment_success,
          severity: "success",
          icon: <SuccessPaymentIcon />,
        });
      }
      if (params?.note) triggerLogEvent(analytics, "pos_note_added");
      if (params?.discount) triggerLogEvent(analytics, "pos_discount_added");
      if (params?.tax_type) triggerLogEvent(analytics, "pos_tax_added");

      const allTxns = await getAllTransactionCount();
      if (allTxns === 5) {
        triggerLogEvent(analytics, "pos_PU");
      }
      handleDemoNavigation(
        "payment",
        `/SaleBook/PaymentSuccess/${invoice?.id}`,
      );
      Sync(user.phone_number, navigate);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const addCustomerToTrns = async (invoice, customer) => {
    try {
      let dataForPayment = {
        outstanding: customer.total_outstanding_payment + Number(totalAmount),
        date: invoice.created_at,
        payment_mode: "Udhaar",
        amount: Number(totalAmount),
        udhaar_note: invoice?.note,
        type: "salebook",
        invoice,
        uid: uuid(),
      };

      await customer.createPayment(dataForPayment);
    } catch (error) {
    }
  };

  const numpadClassName =
    activePaymentMethod === "cash"
      ? `${styles.numpad} ${styles.numpadCash}`
      : `${styles.numpad} ${styles.numpadCenter}`;

  return (
    <div className={styles.container}>
      <TwoColumnWrapperHeader>
        <Box className={twoColumnWrapperHeaderStyles.left}>
          <HeadingText className={"fontSize18 fontWeight500 newFontColor"}>
            {I18n.checkout}
          </HeadingText>
        </Box>
        <Box className={twoColumnWrapperHeaderStyles.right}></Box>
      </TwoColumnWrapperHeader>

      <div className={styles.buttonSection}>
        <UBButton
          id={"cash_btn"}
          className={activePaymentMethod === "cash" ? "active" : ""}
          title={I18n.cash}
          iconComponent={<CashNewIcon />}
          onClick={() => {
            setActivePaymentMethod("cash");
            triggerLogEvent(analytics, "pos_payment_cash");
          }}
        />
        <UBButton
          id={"udhaar_btn"}
          className={activePaymentMethod === "udhaar" ? "active" : ""}
          title={I18n.udhaar}
          onClick={() => {
            setActivePaymentMethod("udhaar");
            triggerLogEvent(analytics, "pos_payment_udhaar");
          }}
          iconComponent={<UdhaarNewIcon />}
        />
        <UBButton
          id={"online_payment_btn"}
          className={activePaymentMethod === "online" ? "active" : ""}
          title={I18n.online}
          onClick={() => {
            setActivePaymentMethod("online");
            triggerLogEvent(analytics, "pos_payment_online");
          }}
          iconComponent={<CardNewIcon />}
        />
      </div>
      <div className={numpadClassName}>
        {activePaymentMethod === "cash" ? (
          <NumKeypad
            totalAmount={totalAmount}
            confirmSale={confirmSale}
            customer={customer}
            isLoadingbtn={isLoading}
            handleCashLogEvents={() =>
              triggerLogEvent(analytics, "pos_cash_start")
            }
          />
        ) : activePaymentMethod === "udhaar" ? (
          <UdhaarCheckout
            totalAmount={totalAmount}
            businessId={business.id}
            confirmSale={confirmSale}
            customer={customer}
            activeSaleOrder={activeSaleOrder}
            isLoadingbtn={isLoading}
            isFocussedRef={isFocussedRef}
          />
        ) : (
          <OnlinePaymentCheckOut
            totalAmount={totalAmount}
            confirmSale={confirmSale}
            isLoadingbtn={isLoading}
          />
        )}
      </div>
    </div>
  );
};

const enhance = withObservables(
  ["activeSaleOrder", "sale_order_line"],
  ({ activeSaleOrder }) => {
    return {
      sale_order_line: ObserveSaleOrderLineBySaleOrder(
        activeSaleOrder?.id ?? "",
      ),
    };
  },
);

export default enhance(CheckoutOrder);
